import * as React from 'react';
import useBooted from 'core/hooks/useBooted';
import { useDispatch, useSelector } from 'react-redux';
import { isMobileMenuOpen } from 'core/redux/menu/selectors';
import { isTelehealthBookingOpen } from 'telehealth/redux/selectors';
import TelehealthBookingModal from 'telehealth/components/TelehealthBookingModal';
import { isFirstVisitOpen } from 'firstVisit/redux/selectors';
import { getCookie, setCookie } from 'lib/cookie';
import { openFirstVisit } from 'firstVisit/redux/actions';
import FirstVisitModal from 'firstVisit/components/FirstVisitModal';
import { isQuoteModalOpen } from 'surgery/redux/quote/selectors';
import { isReferralModalOpen } from 'surgery/redux/referral/selectors';
import { isConsultationOpen } from 'surgery/redux/selectors';
import ConsultancyModal from 'surgery/components/ConsultancyModal';
import TypeformModal from 'surgery/components/TypeformModal';
import { closeReferralModal } from 'surgery/redux/referral/actions';
import { closeQuoteModal } from 'surgery/redux/quote/actions';
import useWindowSize from 'core/hooks/useWindowSize';
import useGooglePhoneConversionTracking from 'core/hooks/useGooglePhoneConversionTracking';
import useAdbarHeight from 'core/hooks/useAdbarHeight';
import { useLocation } from '@reach/router';
import useBrazeSdk from 'analytics/hooks/useBrazeSdk';
import useGAPageTracking from 'analytics/hooks/useGAPageTracking';

function GlobalEffects({ children }: React.PropsWithChildren<unknown>) {
    const { pathname } = useLocation();
    useBooted();
    useGooglePhoneConversionTracking();
    useBrazeSdk();
    useGAPageTracking();
    useAdbarHeight();

    // mobile menu
    const isOpen = useSelector(isMobileMenuOpen);
    React.useEffect(() => {
        if (isOpen) {
            document.documentElement.classList.add('mobile-menu-open');
        } else {
            document.documentElement.classList.remove('mobile-menu-open');

            // make sure to always clean up if a sub-menu didn't close properly
            document.documentElement.classList.remove('mobile-sub-menu-open');
        }
    }, [isOpen]);

    // telehealth
    const telehealthOpen = useSelector(isTelehealthBookingOpen);

    // first visitor
    const dispatch = useDispatch();
    const firstVisitOpen = useSelector(isFirstVisitOpen);
    const isBlogPage = pathname.startsWith('/b/');

    React.useEffect(() => {
        const timer = setTimeout(() => {
            if (!getCookie('first_visit')) {
                if (!isBlogPage) {
                    setCookie('first_visit', new Date().toISOString(), 180);
                    dispatch(openFirstVisit());
                }
            }
        }, 5000);

        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [dispatch, isBlogPage]);

    // quote modal
    const quoteModalOpen = useSelector(isQuoteModalOpen);

    // referralModal
    const referralModalOpen = useSelector(isReferralModalOpen);

    // consultancy modal
    const consultationOpen = useSelector(isConsultationOpen);

    React.useEffect(() => {
        if (consultationOpen || telehealthOpen) {
            document.documentElement.classList.add('disable-scrolling');
        } else {
            document.documentElement.classList.remove('disable-scrolling');
        }
    }, [consultationOpen, telehealthOpen]);

    // monitor and set window height
    // necessary for iOS Safari handling
    const { height } = useWindowSize();
    React.useEffect(() => {
        const style = document.createElement('style');
        const rule = `body {--window-height: ${height}px;}`;

        const styleElement = document.head.appendChild(style);
        style.sheet?.insertRule(rule, style.sheet?.cssRules.length || 0);

        return function cleanup() {
            document.head.removeChild(styleElement);
        };
    }, [height]);

    return (
        <>
            {children}

            {telehealthOpen && <TelehealthBookingModal />}
            {firstVisitOpen && <FirstVisitModal />}
            {/* eslint-disable max-len */}
            {quoteModalOpen && (
                <TypeformModal
                    typeformURL="https://bondvet.typeform.com/to/dWlxzf?embed_domain=bondvet.com&embed_type=PopupText"
                    handleClose={() => {
                        dispatch(closeQuoteModal());
                    }}
                />
            )}
            {referralModalOpen && (
                <TypeformModal
                    typeformURL="https://bondvet.typeform.com/to/dYs2Fy0u?embed_domain=bondvet.com&embed_type=PopupText"
                    handleClose={() => {
                        dispatch(closeReferralModal());
                    }}
                />
            )}
            {/* eslint-enable max-len */}
            {consultationOpen && <ConsultancyModal />}
        </>
    );
}

export default GlobalEffects;
